// Persistent TinyMCE-wrapper. Designad att monteras EN gång och hålla sig vid liv
// under hela modal:ens livstid — innehåll byts via editor.setContent() istället
// för full re-init när användaren växlar mellan textblock.
//
// Föräldern (EmailBuilderModal) håller editorRef:en och styr `display: none`
// när inget textblock är aktivt — TinyMCE förblir mountad men osynlig.

import { Editor as TinyMCE } from '@tinymce/tinymce-react';
import { forwardRef, useImperativeHandle, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { DataFieldsEnum } from 'src/shared/models/enums/dataFields.enum';

// Vi exponerar bara de metoder vi använder från TinyMCE-instansen.
export interface PersistentTinyMceHandle {
  setContent: (html: string) => void;
  getContent: () => string;
}

type Props = {
  // Initial content sätts vid första mount. Efterföljande byten görs via ref.setContent().
  initialContent?: string;
  onChange?: (html: string) => void;
  // Notifieras när TinyMCE-instansen är redo. Föräldern använder detta för att
  // ladda in första textblockets innehåll om ett sådant blev valt innan init.
  onReady?: () => void;
  height?: number;
};

export const PersistentTinyMce = forwardRef<PersistentTinyMceHandle, Props>((props, ref) => {
  const { initialContent = '', onChange, onReady, height = 480 } = props;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const editorRef = useRef<any>(null);
  const { t, i18n } = useTranslation(['common']);
  const language = i18n.language === 'sv' ? 'sv_SE' : 'en_GB';

  useImperativeHandle(
    ref,
    () => ({
      setContent: (html: string) => {
        if (editorRef.current) {
          editorRef.current.setContent(html);
        }
      },
      getContent: () => {
        if (editorRef.current) {
          return editorRef.current.getContent();
        }
        return '';
      },
    }),
    [],
  );

  return (
    <TinyMCE
      initialValue={initialContent}
      onInit={(_evt, editor) => {
        editorRef.current = editor;
        if (onReady) onReady();
      }}
      onEditorChange={(content) => {
        if (onChange) onChange(content);
      }}
      init={{
        height,
        resize: false,
        menubar: 'custom',
        statusbar: false,
        elementpath: false,
        language,
        language_url: `/langs/${language}.js`,
        convert_urls: false,
        // noneditable-regexp gör att [prop.xxx]-tokens renderas som låsta chips
        // i editorn — användaren ska inte kunna placera markören mitt i en token.
        noneditable_noneditable_class: 'is-locked',
        noneditable_regexp: /\[[^\]]+\]/g,
        plugins: ['lists link image paste'],
        toolbar: 'undo redo | bold italic underline | bullist numlist | alignleft aligncenter alignright | link image | removeformat',
        // Datafält-meny — speglar logiken i ControlledTextEditor så att
        // designläget får samma stöd för [prop.xxx]-tokens som "enkel redigering".
        menu: {
          custom: {
            title: t('common:form.dataField') as string,
            items: Object.values(DataFieldsEnum)
              .map((item) => `datafield-${item}`)
              .join(' '),
          },
        },
        // Tvingar TinyMCE att visa webbläsarens native contextmenu vid högerklick
        // (för Klistra in/Paste). Link-pluginen auto-registrerar annars en egen
        // "Länk"-menypost som kapar högerklick. Tomt värde + never_use_native=false
        // är den kombination som faktiskt släpper igenom native menyn i TinyMCE 5.
        contextmenu: '',
        contextmenu_never_use_native: false,
        browser_spellcheck: true,
        paste_data_images: true,
        setup: (editor) => {
          Object.values(DataFieldsEnum).forEach((item) => {
            editor.ui.registry.addMenuItem(`datafield-${item}`, {
              type: 'menuitem',
              text: t(`common:dataFields.${item}`) as string,
              onAction() {
                editor.insertContent(`[prop.${item}]`);
              },
            });
          });
        },
        content_style: `
          body { font-family: Helvetica, Arial, sans-serif; font-size: 15px; color: #333; padding: 0; margin: 0; }
          .is-locked,
          .mceNonEditable {
            background-color: #D6F0FF;
            padding: 1px 0;
            color: #44719B;
            font-family: SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
            font-size: 0.9375em;
          }
        `,
      }}
    />
  );
});

PersistentTinyMce.displayName = 'PersistentTinyMce';
